"use client";

import { useState, useEffect } from "react";
import axios from "axios";
import { useMutation } from "react-query";
import { useForm, ValidationError } from "@formspree/react";
import { useToast } from "../../../components/Toast/toast"; // Adjust path as needed
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
<link rel="canonical" href="https://m2hse.co.uk/contact-us" />;
const ContactModal = ({
  isOpen: externalIsOpen,
  onClose: externalOnClose,
  buttonText = "Enquire Now",
  showButton = true,
  formspreeId = "xeqypwnz",
  redirectPath = "/thank-you",
}) => {
  // Use internal state if no external state is provided
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isModalOpen =
    externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const toggleModal = () => {
    if (externalOnClose) {
      externalOnClose();
    } else {
      setInternalIsOpen(!internalIsOpen);
    }
  };

  const navigate = useNavigate();
  const { addToast } = useToast();

  // Handle click outside to close
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape" && isModalOpen) {
        toggleModal();
      }
    };

    document.addEventListener("keydown", handleEscKey);
    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [isModalOpen]);

  // API call to create person
  const useCreatePerson = async (formData) => {
    const response = await axios.post("people/create", formData);
    return response.data;
  };

  const [state, handleFormspreeSubmit] = useForm(formspreeId);
  const { isLoading, mutate } = useMutation(useCreatePerson);

  const handleSubmitForm = async (e) => {
    e.preventDefault();

    const form = e.target;
    const formData = new FormData(form);

    const formValues = {
      firstname: formData.get("firstname"),
      lastname: formData.get("lastname"),
      email: formData.get("email"),
      phone: formData.get("phone"),
      message: formData.get("message"),
      page: window.location.pathname,
      utm_source: localStorage.getItem("utm_source") || "direct",
      utm_medium: localStorage.getItem("utm_medium") || "none",
      utm_id: localStorage.getItem("utm_id") || "none",
      utm_campaign: localStorage.getItem("utm_campaign") || "none",
      timeSpentInSeconds: calculateTimeSpent(),
      firstPageVisited: localStorage.getItem("first_page") || "unknown",
    };

    try {
      // Submit to formspree
      await handleFormspreeSubmit(e);

      // Submit to your API
      await mutate(formValues);

      // Clean up localStorage
      localStorage.removeItem("entry_time");
      localStorage.removeItem("first_page");

      // Show success message
      addToast(
        "We have received your query, please rest assured our team will get back to you",
        "success"
      );

      // Reset form and close modal
      form.reset();
      toggleModal();

      // Redirect
      navigate(redirectPath);
    } catch (error) {
      addToast("An error occurred. Please try again later.", "error");
    }
  };

  const calculateTimeSpent = () => {
    const entryTime = Number.parseInt(
      localStorage.getItem("entry_time") || "0",
      10
    );
    const currentTime = Date.now();
    return Math.floor((currentTime - entryTime) / 1000);
  };

  return (
    <>
      {showButton && (
        <button
          onClick={toggleModal}
          className="bg-[#FF3131] text-white py-2.5 px-6 rounded-lg text-base font-medium hover:bg-red-600 transition-colors duration-300 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
        >
          {buttonText}
        </button>
      )}
      <Helmet>
        <link rel="canonical" href="https://m2hse.co.uk/contact-us" />;
      </Helmet>

      {isModalOpen && (
        <div
          className="fixed top-52 inset-0 z-50 overflow-y-auto"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div
              className="fixed inset-0 bg-black bg-opacity-60 transition-opacity"
              aria-hidden="true"
              onClick={toggleModal}
            ></div>

            {/* Modal panel */}
            <div className="inline-block align-bottom rounded-xl  bg-white dark:bg-gray-800  text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full">
              {/* Header */}
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Contact Us
                </h3>
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  onClick={toggleModal}
                >
                  <span className="sr-only">Close</span>
                  <svg
                    className="h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Form */}
              <div className="px-6 py-4">
                <form
                  onSubmit={handleSubmitForm}
                  data-clarity-unmask="true"
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* First Name */}
                    <div>
                      <label
                        htmlFor="firstname"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        First Name<span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="firstname"
                        id="firstname"
                        placeholder="Your first name"
                        required
                        className="w-full h-8 text-sm px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white"
                      />
                    </div>

                    {/* Last Name */}
                    <div>
                      <label
                        htmlFor="lastname"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        Last Name<span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="lastname"
                        id="lastname"
                        placeholder="Your last name"
                        required
                        className="w-full h-8 text-sm px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      Email<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      placeholder="Your email address"
                      required
                      className="w-full h-8 text-sm px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white"
                    />
                    <ValidationError
                      prefix="Email"
                      field="email"
                      errors={state.errors}
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      Phone<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      id="phone"
                      placeholder="Your phone number"
                      required
                      className="w-full h-8 text-sm px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      Message (Optional)
                    </label>
                    <textarea
                      name="message"
                      id="message"
                      rows={4}
                      placeholder="Please drop your query, we would love to help you"
                      className="w-full text-sm px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white"
                    ></textarea>
                  </div>

                  {/* Submit Button */}
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={isLoading || state.submitting}
                      className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-1 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading || state.submitting ? (
                        <span className="flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Submitting...
                        </span>
                      ) : (
                        "Submit"
                      )}
                    </button>
                  </div>
                  <input type="hidden" id="zc_gad" name="zc_gad" value="" />
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ContactModal;
